import React, { useRef, useEffect, useState, Fragment } from 'react';
import Tick from '@pqina/flip';
import '@pqina/flip/dist/flip.min.css';
import EventTimeLabels from './eventTimeLabels';

interface EventCountdownProps {
    value: string;
    countUp?: boolean;
}

const EventCountdown: React.FC<EventCountdownProps> = ({ value, countUp }) => {
    const divRef = useRef<HTMLDivElement>(null);
    const tickRef = useRef<any>(null); // Replace 'any' with the appropriate type if available

    const [tickValue, setTickValue] = useState<string>(value);
    const [showHours, setShowHours] = useState<boolean>(false);

    // Make the Tick instance and store it in the refs
    useEffect(() => {
        const didInit = (tick: any) => {
            tickRef.current = tick;
        };

        const currDiv = divRef.current;
        const tickValue = tickRef.current;
        Tick.DOM.create(currDiv, {
            value,
            didInit
        });

        return () => Tick.DOM.destroy(tickValue);
    }, [value]);

    // Start the Tick.down process
    useEffect(() => {
        let counter: any = null;
        if (countUp) {
            counter = Tick.count.up(value, {
                format: ['h', 'm', 's']
            });
        } else {
            counter = Tick.count.down(value, {
                format: ['h', 'm', 's']
            });
        }

        // When the counter updates, update React's state value
        counter.onupdate = (value: string) => {
            setTickValue(value);
        };

        const time = new Date(value);
        let shouldShowHours = false;
        let shouldChangeShowHours = false;
        let changeTime = 0;

        if (countUp) {
            const hourAgo = new Date();
            hourAgo.setHours(hourAgo.getHours() - 1);
            shouldShowHours = time < hourAgo;
            shouldChangeShowHours = !shouldShowHours;
            if (shouldChangeShowHours) {
                changeTime = time.getTime() - hourAgo.getTime();
            }
        } else {
            const hourFromNow = new Date();
            hourFromNow.setHours(hourFromNow.getHours() + 1);
            shouldShowHours = time > hourFromNow;
            shouldChangeShowHours = shouldShowHours;
            if (shouldChangeShowHours) {
                changeTime = time.getTime() - hourFromNow.getTime();
            }
        }

        setShowHours(shouldShowHours);

        let changeHoursTimeout: any = null;
        if (shouldChangeShowHours) {
            changeHoursTimeout = setTimeout(() => {
                setShowHours(!shouldShowHours);
            }, changeTime);
        }

        return () => {
            counter.timer.stop();
            if (changeHoursTimeout) {
                clearTimeout(changeHoursTimeout);
            }
        };
    }, [countUp, value]);

    // When the tickValue is updated, update the Tick.DOM element
    useEffect(() => {
        if (tickRef.current) {
            tickRef.current.value = {
                hours: tickValue[0],
                mins: tickValue[1],
                secs: tickValue[2]
            };
        }
    }, [tickValue]);

    return (
        <Fragment>
            <div className="tick" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
                <div data-repeat="true" data-layout="horizontal fit">
                    <div className="tick-group" style={{ fontSize: 24 }}>
                        <div
                            ref={divRef}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: '1.5rem'
                            }}
                        >
                            <div
                                data-key="hours"
                                style={{ marginRight: '2rem', display: showHours ? 'block' : 'none' }}
                                data-repeat="true"
                                data-transform="pad(00) -> split -> delay"
                            >
                                <span data-view="flip"></span>
                            </div>

                            <div
                                data-key="mins"
                                style={{ marginRight: showHours ? 0 : '2rem' }}
                                data-repeat="true"
                                data-transform="pad(00) -> split -> delay"
                            >
                                <span data-view="flip"></span>
                            </div>

                            <div
                                data-key="secs"
                                style={{ display: showHours ? 'none' : 'block' }}
                                data-repeat="true"
                                data-transform="pad(00) -> split -> delay"
                            >
                                <span data-view="flip"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <EventTimeLabels showHours={showHours} />
        </Fragment>
    );
};

export default EventCountdown;
